import flatten from 'flat';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { FormErrors, RegisteredFieldState } from 'redux-form';
import { CannedQuestionName } from '../api/models/survey';
import {
    CANNED_QUESTION_EMAIL_ADDRESS,
    CANNED_QUESTION_FIRST_NAME,
    CANNED_QUESTION_LAST_NAME,
} from '../containers/checkout/constants';
import { CheckoutFormData, CheckoutFormErrorType } from '../state/form';
import { stripAllBlanks } from './general';

export const flattenFormData = (
    formData?: Array<unknown> | Record<string, unknown>,
): Record<string, unknown> =>
    flatten(formData ?? {}, {
        safe: true,
    });

export const focusOnFirstError = (
    registeredFields: string[],
    errors:
        | FormErrors<CheckoutFormData, CheckoutFormErrorType>
        | undefined = {},
) => {
    const errorFieldNames = flattenFormData(errors as CheckoutFormErrorType);

    registeredFields.find((field) => {
        if (errorFieldNames[field]) {
            const elem: HTMLElement | null = document.querySelector(
                `input[name="${field}"], select[name="${field}"], input[aria-invalid="true"], select[aria-invalid="true"]`,
            );
            if (elem) {
                elem.focus();
                return true;
            }
        }
        return false;
    });
};

export const getActiveFieldsPerSectionNamespace = (
    fields: RegisteredFieldState[] = [],
) =>
    Object.keys(fields).reduce((fieldsPerNamespace, key) => {
        if (!get(fields, key).count) {
            return fieldsPerNamespace;
        }

        const keyParts = key.split('.');
        const namespace = keyParts[0];
        const field = keyParts[1];
        const fieldsInKeyNamespace = get(fieldsPerNamespace, namespace);

        return {
            ...fieldsPerNamespace,
            [namespace]: fieldsInKeyNamespace
                ? [...fieldsInKeyNamespace, field]
                : [field],
        };
    }, {});

export const hasErrorInFields = (
    fields: Array<string>,
    syncErrors?: FormErrors<{}, string>,
    asyncErrors?: FormErrors<{}, string>,
) =>
    fields?.some((error) => {
        return !!get(syncErrors, error) || !!get(asyncErrors, error);
    }) ?? false;

export const normalizePaymentMethodInfo = (formData: Record<string, any>) => {
    if (isEmpty(formData) || isEmpty(formData.payment)) {
        return formData;
    }

    return {
        ...formData,
        payment: {
            ...formData.payment,
            ...(formData.payment?.cardExpirationDate
                ? {
                      cardExpirationDate: stripAllBlanks(
                          formData.payment.cardExpirationDate,
                      ),
                  }
                : {}),
            ...(formData.payment?.creditCardNumber
                ? {
                      creditCardNumber: stripAllBlanks(
                          formData.payment.creditCardNumber,
                      ),
                  }
                : {}),
        },
    };
};

const filterErrorsForSingleAttendee = (
    formValues: Record<string, any>,
    errors: FormErrors<{}, string> | undefined,
): Record<string, any> =>
    Object.entries(formValues).reduce<Record<string, any>>(
        (accum, [rootFieldKey, rootFieldValue]) => {
            if (typeof rootFieldValue === 'object') {
                const filteredValues = filterErrorsForSingleAttendee(
                    rootFieldValue,
                    errors?.[rootFieldKey as keyof typeof errors],
                );

                return !isEmpty(filteredValues)
                    ? {
                          ...accum,
                          [rootFieldKey]: filteredValues,
                      }
                    : accum;
            }

            return isEmpty(errors?.[rootFieldKey as keyof typeof errors])
                ? {
                      ...accum,
                      [rootFieldKey]: rootFieldValue,
                  }
                : accum;
        },
        {},
    );

export const filterErroredFieldsFromFormValues = (
    formValues: Record<string, any>,
    errors: FormErrors<{}, string>,
) =>
    Object.keys(formValues).reduce(
        (formValuesCleaned, attendeeKey) => ({
            ...formValuesCleaned,
            [attendeeKey as keyof typeof formValuesCleaned]:
                filterErrorsForSingleAttendee(
                    formValues[attendeeKey],
                    errors[attendeeKey as keyof typeof errors],
                ),
        }),
        {},
    );

export const hasSomeBasicInfoQuestions = (
    questions: { name: CannedQuestionName }[],
) => {
    const basicInfoQuestions = new Set([
        CANNED_QUESTION_FIRST_NAME,
        CANNED_QUESTION_LAST_NAME,
        CANNED_QUESTION_EMAIL_ADDRESS,
    ]);
    return questions.some(({ name }) => basicInfoQuestions.has(name));
};
